import type { SearchFieldConfig } from '../types'

// An operand is a word or phrase. `modified` is true when it carries a leading +, -
// or ! (required or excluded), and `plain` is false when it is modified or names its
// field, as in title:Trump. `text` leaves the field out. A field before a group, as
// in -title:(Putin Xi), keeps its exclusion.
type Token
  = { kind: 'operand', text: string, plain: boolean, modified: boolean }
    | { kind: 'field', excluded: boolean, modified: boolean }
    | { kind: 'open' | 'close' | 'binary' | 'not' }

interface Parsed {
  tokens: Token[]
  // Offsets of characters that are text, not syntax: a colon that follows no field
  // name, as in "EU:s" or "14:00", and a sign before a number, as in "-20 grader".
  literals: number[]
}

interface Colon {
  raw: number
  word: number
  text: number
}

const BINARY = new Set(['AND', 'OR', '&&', '||'])

// The parser's whitespace; a no-break space is part of a word.
const SPACE = /[ \t\n\r\u3000]/
const SEPARATOR = /[ \t\n\r\u3000()]/
const RANGE = /^ *[^ ]+ +TO +[^ ]+ *$/

// Index of the closing character, skipping backslash-escaped ones, or -1.
function closingIndex(raw: string, from: number, close: string): number {
  for (let i = from; i < raw.length; i++) {
    if (raw[i] === '\\') {
      i++
    } else if (raw[i] === close) {
      return i
    }
  }
  return -1
}

// Checks a term, the word without its modifier or field, against what the query
// string parser rejects. `/` opens a regular expression, which free text like "24/7"
// never means, so it counts as invalid.
function isValidTerm(term: string): boolean {
  if (!term || '+-!~^'.includes(term[0])) {
    return false
  }

  let boosts = 0
  let slops = 0
  for (let i = 0; i < term.length; i++) {
    const c = term[i]
    if (c === '\\') {
      i++
    } else if ('/!]}'.includes(c)) {
      return false
    } else if (c === '^' && (++boosts > 1 || !/\d/.test(term[i + 1] ?? ''))) {
      return false
    } else if (c === '~' && ++slops > 1) {
      return false
    }
  }
  return true
}

// Only the view's own field names make a field: "TT:s" and "EU:s budget" are text.
function tokenize(raw: string, fields: SearchFieldConfig[]): Parsed | undefined {
  const names = new Set<string>(fields.flatMap((f) => [f.syntaxAlias, f.fieldPath]))
  const tokens: Token[] = []
  const literals: number[] = []
  let i = 0

  while (i < raw.length) {
    const c = raw[i]

    if (SPACE.test(c)) {
      i++
      continue
    }

    if (c === '(' || c === ')') {
      tokens.push({ kind: c === '(' ? 'open' : 'close' })
      i++
      continue
    }

    // `word` stands a phrase or range in for a placeholder so it can be checked as
    // one word; `text` keeps what was typed.
    const start = i
    let word = ''
    let text = ''
    const colons: Colon[] = []
    while (i < raw.length && !SEPARATOR.test(raw[i])) {
      const ch = raw[i]
      if (ch === ':') {
        colons.push({ raw: i, word: word.length, text: text.length })
      }
      if (ch === '\\') {
        if (i + 1 === raw.length) {
          return undefined
        }
        word += raw.slice(i, i + 2)
        text += raw.slice(i, i + 2)
        i += 2
      } else if (ch === '"' || ch === '[' || ch === '{') {
        const end = closingIndex(raw, i + 1, ch === '"' ? '"' : ch === '[' ? ']' : '}')
        if (end === -1 || (ch !== '"' && !RANGE.test(raw.slice(i + 1, end)))) {
          return undefined
        }
        word += 'x'
        text += raw.slice(i + 1, end)
        i = end + 1
      } else {
        word += ch
        text += ch
        i++
      }
    }

    const literal = /^[+-]\d/.test(word)
    const modifier = literal ? '' : /^[+\-!]?/.exec(word)?.[0] ?? ''
    const first = colons.at(0)
    const field = first && names.has(word.slice(modifier.length, first.word)) ? first : undefined
    const term = word.slice(field ? field.word + 1 : literal ? 1 : modifier.length)
    literals.push(...colons.filter((colon) => colon !== field).map((colon) => colon.raw))
    if (literal) {
      literals.push(start)
    }

    if (BINARY.has(word)) {
      tokens.push({ kind: 'binary' })
    } else if (word === 'NOT') {
      tokens.push({ kind: 'not' })
    } else if (field && !term) {
      const excluded = modifier === '-' || modifier === '!'
      tokens.push({ kind: 'field', excluded, modified: !!modifier })
    } else if (isValidTerm(term)) {
      tokens.push({
        kind: 'operand',
        text: literal ? `\\${text}` : field ? modifier + text.slice(field.text + 1) : text,
        plain: !field && !modifier,
        modified: !!modifier
      })
    } else {
      return undefined
    }
  }

  return { tokens, literals }
}

// Whether the tokens form a query: operators between operands, groups closed, a
// plain word or group after a field, and after NOT one with no modifier of its own.
function isWellFormed(tokens: Token[]): boolean {
  let depth = 0
  let expectOperand = true
  let previous: Token | undefined

  for (const token of tokens) {
    const unmodified = (token.kind === 'operand' || token.kind === 'field') && !token.modified
    const plainWord = token.kind === 'operand' && token.plain
    if (previous?.kind === 'field' && !(token.kind === 'open' || plainWord)) {
      return false
    }
    if (previous?.kind === 'not' && !(token.kind === 'open' || unmodified)) {
      return false
    }
    previous = token

    switch (token.kind) {
      case 'operand':
        expectOperand = false
        break
      case 'open':
        depth++
        expectOperand = true
        break
      case 'close':
        if (expectOperand || --depth < 0) {
          return false
        }
        break
      case 'binary':
        if (expectOperand) {
          return false
        }
        expectOperand = true
        break
      default:
        expectOperand = true
    }
  }

  return depth === 0 && !expectOperand
}

// Text the query string parser accepts, or undefined.
function parse(raw: string, fields: SearchFieldConfig[]): Parsed | undefined {
  const parsed = tokenize(raw, fields)
  return parsed && isWellFormed(parsed.tokens) ? parsed : undefined
}

// Parentheses alone are not syntax: "Ulf Kristersson (M)" names a party.
function usesSyntax(tokens: Token[]): boolean {
  return tokens.some((token) => token.kind === 'operand'
    ? !token.plain
    : token.kind !== 'open' && token.kind !== 'close')
}

/**
 * Whether OpenSearch's query string parser accepts the text. Covers what typing and
 * everyday news text produce (open quotes and brackets, dangling operators, `24/7`),
 * not the whole grammar.
 */
export function isValidQuerySyntax(raw: string, fields: SearchFieldConfig[]): boolean {
  return parse(raw, fields) !== undefined
}

/**
 * Whether the text is valid syntax that uses operators, required or excluded terms,
 * grouping or fields - anything plain words and a quoted phrase cannot express.
 */
export function isBooleanQuery(raw: string, fields: SearchFieldConfig[]): boolean {
  const parsed = parse(raw, fields)
  return parsed ? usesSyntax(parsed.tokens) : false
}

/**
 * The query string a boolean query is sent as, with what is text rather than syntax
 * escaped, or undefined when the text is not one.
 */
export function booleanQueryString(raw: string, fields: SearchFieldConfig[]): string | undefined {
  const parsed = parse(raw, fields)
  if (!parsed || !usesSyntax(parsed.tokens)) {
    return undefined
  }

  let result = raw
  for (const offset of [...parsed.literals].sort((a, b) => b - a)) {
    result = `${result.slice(0, offset)}\\${result.slice(offset)}`
  }
  return result
}

/**
 * The words and phrases the text searches for, without operators, fields, modifiers
 * or anything it excludes. Text that is not valid syntax is split on whitespace.
 */
export function searchWords(raw: string, fields: SearchFieldConfig[]): string[] {
  const tokens = parse(raw, fields)?.tokens
  if (!tokens) {
    return raw.split(/\s+/).filter(Boolean)
  }

  const words: string[] = []
  let depth = 0
  let skipFrom: number | undefined
  let excludeNext = false

  for (const token of tokens) {
    if (skipFrom !== undefined) {
      if (token.kind === 'open') {
        depth++
      } else if (token.kind === 'close' && --depth === skipFrom) {
        skipFrom = undefined
      }
      continue
    }

    if (token.kind === 'not' || (token.kind === 'field' && token.excluded)) {
      excludeNext = true
    } else if (token.kind === 'open') {
      if (excludeNext) {
        skipFrom = depth
        excludeNext = false
      }
      depth++
    } else if (token.kind === 'close') {
      depth--
    } else if (token.kind === 'operand') {
      const excluded = excludeNext || token.text.startsWith('-') || token.text.startsWith('!')
      excludeNext = false
      const word = token.text
        .replace(/^[+\-!]+/, '')
        .replace(/(\^\d+(\.\d+)?|~\d*|[*?]+)+$/, '')
        .replace(/\\(.)/g, '$1')
      if (!excluded && word) {
        words.push(word)
      }
    }
  }

  return words
}
